import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../hooks/useToast';
import Modal from '../common/Modal';
import ListLoadError from './ListLoadError';
import api from '../../services/api';
import styles from './IntakeFormEditorModal.module.css';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'multi_select', label: 'Multi Select (Checkboxes)' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'height', label: 'Height (ft/in)' },
  { value: 'weight', label: 'Weight (lbs)' }
];

/**
 * IntakeFormEditorModal - Modal for editing intake form questions
 * Props:
 *   isOpen - Boolean to control modal visibility
 *   onClose - Callback when modal is closed
 *   productId - Product ID to edit intake form for
 *   productName - Product name for display
 *   onSave - Callback after successful save
 */
const IntakeFormEditorModal = ({
  isOpen,
  onClose,
  productId,
  productName = 'Product',
  onSave
}) => {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [banksError, setBanksError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [commonQuestions, setCommonQuestions] = useState([]);
  const [globalQuestions, setGlobalQuestions] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showGlobalQuestions, setShowGlobalQuestions] = useState(true);
  const [showCommonQuestions, setShowCommonQuestions] = useState(false);

  // Load form and common questions when modal opens
  useEffect(() => {
    if (!isOpen || !productId) return;

    const loadData = async () => {
      setLoading(true);
      setLoadError(false);
      setBanksError(false);
      try {
        const [formRes, questionsRes, globalRes] = await Promise.all([
          // A 404 genuinely means "this product has no intake form yet" and MUST keep
          // taking the new-form path below. Anything else - 500, a network failure, an
          // auth error - is us failing to READ the form, which is not the same fact.
          // The old blanket `.catch` collapsed both into `{ form: null }`, so a failed
          // read rendered an empty editor whose Save POSTs a SECOND form over a product
          // that already has one.
          api.get(`/admin/intake-forms/product/${productId}`).catch((error) => {
            if (error?.status === 404) return { form: null };
            throw error;
          }),
          // A failed bank read is RECORDED, not swallowed. Left at `[]` it is byte-identical
          // to a tenant whose banks are genuinely empty, so the toggle below opened onto
          // nothing at all - the exact confusion ListLoadError exists to end.
          api.get('/admin/intake-forms/common-questions').catch(() => ({ categories: [], __loadFailed: true })),
          api.get('/admin/intake-forms/global-questions').catch(() => ({ questions: [], __loadFailed: true }))
        ]);

        setCommonQuestions(questionsRes.categories || []);
        setGlobalQuestions(globalRes.questions || []);
        setBanksError(Boolean(questionsRes.__loadFailed || globalRes.__loadFailed));

        // Set form data or create empty form
        if (formRes.form) {
          setForm({
            ...formRes.form,
            fields: formRes.form.fields || [],
            settings: formRes.form.settings || { showProgressBar: true, requiresDriversLicense: true }
          });
        } else {
          setForm({
            name: `${productName} Intake Form`,
            fields: [],
            settings: {
              showProgressBar: true,
              requiresDriversLicense: true
            }
          });
        }
      } catch (error) {
        // Deliberately NOT a fabricated blank form any more. An empty editor is
        // indistinguishable from a product whose form genuinely has no questions -
        // measured byte-identical, 216 characters either way - and it offers
        // `+ Add First Question` and `Save Form`, which is how a failed read turns
        // into a duplicate record.
        setForm(null);
        setLoadError(true);
        console.error('Failed to load intake form:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, productId, productName, reloadKey]);

  const handleSaveForm = async () => {
    if (!productId || !form) return;

    setSaving(true);
    try {
      const formId = form._id || form.id;
      if (formId) {
        await api.patch(`/admin/intake-forms/${formId}`, form);
        toast.success('Intake form updated');
      } else {
        const response = await api.post('/admin/intake-forms', {
          ...form,
          productId
        });
        setForm(response.form);
        toast.success('Intake form created');
      }
      onSave?.();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const addField = useCallback(() => {
    if (!form) return;
    const currentFields = Array.isArray(form.fields) ? form.fields : [];
    const newField = {
      fieldId: `field_${Date.now()}`,
      type: 'text',
      label: 'New Question',
      section: 'General',
      order: 0,
      validation: []
    };
    const updatedFields = [newField, ...currentFields].map((f, i) => ({ ...f, order: i }));
    setForm((prevForm) => ({ ...prevForm, fields: updatedFields }));
    setEditingField(newField.fieldId);
  }, [form]);

  const updateField = useCallback((fieldId, updates) => {
    if (!form || !form.fields) return;
    setForm({
      ...form,
      fields: form.fields.map((f) =>
        f.fieldId === fieldId ? { ...f, ...updates } : f
      )
    });
  }, [form]);

  const removeField = useCallback((fieldId) => {
    if (!form || !form.fields) return;
    setForm({
      ...form,
      fields: form.fields.filter((f) => f.fieldId !== fieldId)
    });
  }, [form]);

  const moveField = useCallback((fieldId, direction) => {
    if (!form || !form.fields) return;
    const index = form.fields.findIndex((f) => f.fieldId === fieldId);
    if ((direction === -1 && index === 0) || (direction === 1 && index === form.fields.length - 1)) {
      return;
    }
    const newFields = [...form.fields];
    const [moved] = newFields.splice(index, 1);
    newFields.splice(index + direction, 0, moved);
    const updatedFields = newFields.map((f, i) => ({ ...f, order: i }));
    setForm({ ...form, fields: updatedFields });
  }, [form]);

  const moveFieldToPosition = useCallback((fieldId, newPosition) => {
    if (!form || !form.fields) return;
    const currentIndex = form.fields.findIndex((f) => f.fieldId === fieldId);
    if (currentIndex === -1) return;

    const targetIndex = Math.max(0, Math.min(form.fields.length - 1, newPosition - 1));
    if (targetIndex === currentIndex) return;

    const newFields = [...form.fields];
    const [moved] = newFields.splice(currentIndex, 1);
    newFields.splice(targetIndex, 0, moved);
    const updatedFields = newFields.map((f, i) => ({ ...f, order: i }));
    setForm({ ...form, fields: updatedFields });
  }, [form]);

  const toggleRequired = useCallback((fieldId) => {
    if (!form || !form.fields) return;
    const field = form.fields.find((f) => f.fieldId === fieldId);
    if (!field) return;
    const hasRequired = field.validation?.some((v) => v.type === 'required');

    updateField(fieldId, {
      validation: hasRequired
        ? field.validation.filter((v) => v.type !== 'required')
        : [...(field.validation || []), { type: 'required', message: 'This field is required' }]
    });
  }, [form, updateField]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const addCommonQuestion = useCallback((question) => {
    if (!form) return;

    const exists = form.fields?.some(
      (f) => f.label.toLowerCase().trim() === question.label.toLowerCase().trim()
    );

    if (exists) {
      toast.error('This question is already in the form');
      return;
    }

    const currentFields = Array.isArray(form.fields) ? form.fields : [];
    const newField = {
      fieldId: `field_${Date.now()}`,
      type: question.type,
      label: question.label,
      section: question.section || 'General',
      order: 0,
      validation: []
    };

    const updatedFields = [newField, ...currentFields].map((f, i) => ({ ...f, order: i }));
    setForm((prevForm) => ({ ...prevForm, fields: updatedFields }));
    toast.success(`Added "${question.label}" to form`);
  }, [form, toast]);

  const modalTitle = `Intake Form: ${productName}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} size="large">
      <div className={styles.editor}>
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading form...</p>
          </div>
        ) : loadError ? (
          <ListLoadError
            message="Could not load this intake form"
            onRetry={() => setReloadKey((key) => key + 1)}
          />
        ) : form ? (
          <>
            {/* Toggle for Common Questions */}
            <button
              type="button"
              className={`${styles.commonQuestionsToggle} ${showCommonQuestions ? styles.active : ''}`}
              onClick={() => setShowCommonQuestions(!showCommonQuestions)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {showCommonQuestions ? 'Hide' : 'Show'} Common Questions
            </button>

            {/* Common Questions Section */}
            {showCommonQuestions && (banksError || globalQuestions.length > 0 || commonQuestions.length > 0) && (
              <div className={styles.commonQuestionsSection}>
                {banksError && (
                  <ListLoadError
                    message="Could not load the common question banks"
                    onRetry={() => setReloadKey((key) => key + 1)}
                  />
                )}
                <div className={styles.categoriesList}>
                  {/* Global Common Questions */}
                  {globalQuestions.length > 0 && (
                    <div className={styles.categoryAccordion}>
                      <button
                        type="button"
                        className={`${styles.categoryHeader} ${styles.globalHeader} ${showGlobalQuestions ? styles.expanded : ''}`}
                        onClick={() => setShowGlobalQuestions(!showGlobalQuestions)}
                      >
                        <span className={styles.categoryName}>
                          <span className={styles.globalBadge}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          </span>
                          Global (All Categories)
                        </span>
                        <span className={styles.categoryCount}>{globalQuestions.length}</span>
                        <span className={styles.expandIcon}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points={showGlobalQuestions ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                          </svg>
                        </span>
                      </button>
                      {showGlobalQuestions && (
                        <div className={styles.questionsList}>
                          {globalQuestions.map((question, idx) => (
                            <button
                              key={idx}
                              type="button"
                              className={`${styles.questionItem} ${styles.clickable}`}
                              onClick={() => addCommonQuestion(question)}
                              title={`Add "${question.label}" to form`}
                            >
                              <div className={styles.questionLabel}>
                                {question.label}
                                <span className={styles.addIndicator}>+</span>
                              </div>
                              <div className={styles.questionMeta}>
                                <span className={styles.questionType}>
                                  {FIELD_TYPES.find((t) => t.value === question.type)?.label || question.type}
                                </span>
                                <span className={styles.usageCount}>
                                  Used {question.usageCount}x
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Per-Category Common Questions */}
                  {commonQuestions.map((category) => (
                    <div key={category.categoryId} className={styles.categoryAccordion}>
                      <button
                        type="button"
                        className={`${styles.categoryHeader} ${expandedCategories[category.categoryId] ? styles.expanded : ''}`}
                        onClick={() => toggleCategory(category.categoryId)}
                      >
                        <span className={styles.categoryName}>{category.categoryName}</span>
                        <span className={styles.categoryCount}>{category.questions.length}</span>
                        <span className={styles.expandIcon}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points={expandedCategories[category.categoryId] ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                          </svg>
                        </span>
                      </button>
                      {expandedCategories[category.categoryId] && (
                        <div className={styles.questionsList}>
                          {category.questions.map((question, idx) => (
                            <button
                              key={idx}
                              type="button"
                              className={`${styles.questionItem} ${styles.clickable}`}
                              onClick={() => addCommonQuestion(question)}
                              title={`Add "${question.label}" to form`}
                            >
                              <div className={styles.questionLabel}>
                                {question.label}
                                <span className={styles.addIndicator}>+</span>
                              </div>
                              <div className={styles.questionMeta}>
                                <span className={styles.questionType}>
                                  {FIELD_TYPES.find((t) => t.value === question.type)?.label || question.type}
                                </span>
                                <span className={styles.usageCount}>
                                  Used {question.usageCount}x
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Settings */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Form Settings</h3>
              <div className={styles.settingsRow}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={form.settings?.requiresDriversLicense !== false}
                    onChange={(e) => setForm({
                      ...form,
                      settings: { ...form.settings, requiresDriversLicense: e.target.checked }
                    })}
                  />
                  <span>Require Driver's License Upload</span>
                </label>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={form.settings?.showProgressBar !== false}
                    onChange={(e) => setForm({
                      ...form,
                      settings: { ...form.settings, showProgressBar: e.target.checked }
                    })}
                  />
                  <span>Show Progress Bar</span>
                </label>
              </div>
            </div>

            {/* Fields Header */}
            <div className={styles.fieldsHeader}>
              <h3 className={styles.sectionTitle}>
                Questions ({form.fields?.length || 0})
              </h3>
              <button type="button" className={styles.addBtn} onClick={addField}>
                + Add Question
              </button>
            </div>

            {/* Fields List */}
            <div className={styles.fieldsList}>
              {(!form.fields || form.fields.length === 0) ? (
                <div className={styles.emptyFields}>
                  <p>No questions added yet</p>
                  <button type="button" className={styles.addBtn} onClick={addField}>
                    + Add First Question
                  </button>
                </div>
              ) : (
                form.fields.map((field, index) => (
                  <div key={field.fieldId} className={styles.fieldCard}>
                    <div className={styles.fieldHeader}>
                      <div className={styles.fieldOrder}>
                        <button
                          type="button"
                          onClick={() => moveField(field.fieldId, -1)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="18 15 12 9 6 15" />
                          </svg>
                        </button>
                        <span>{index + 1}</span>
                        <button
                          type="button"
                          onClick={() => moveField(field.fieldId, 1)}
                          disabled={index === form.fields.length - 1}
                          title="Move down"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </button>
                      </div>
                      <div className={styles.fieldActions}>
                        <button
                          type="button"
                          className={styles.editBtn}
                          onClick={() => setEditingField(
                            editingField === field.fieldId ? null : field.fieldId
                          )}
                        >
                          {editingField === field.fieldId ? 'Done' : 'Edit'}
                        </button>
                        <button
                          type="button"
                          className={styles.deleteBtn}
                          onClick={() => removeField(field.fieldId)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {editingField === field.fieldId ? (
                      <div className={styles.fieldEdit}>
                        <div className={styles.fieldRow}>
                          <label>Question</label>
                          <input
                            type="text"
                            value={field.label}
                            onChange={(e) => updateField(field.fieldId, { label: e.target.value })}
                            placeholder="Enter question text"
                          />
                        </div>

                        <div className={styles.fieldRowGrid}>
                          <div className={styles.fieldRow}>
                            <label>Type</label>
                            <select
                              value={field.type}
                              onChange={(e) => updateField(field.fieldId, { type: e.target.value })}
                            >
                              {FIELD_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          </div>

                          <div className={styles.fieldRow}>
                            <label>Position</label>
                            <select
                              value={index + 1}
                              onChange={(e) => moveFieldToPosition(field.fieldId, parseInt(e.target.value))}
                            >
                              {form.fields.map((_, i) => (
                                <option key={i} value={i + 1}>{i + 1}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className={styles.fieldRow}>
                          <label>Section</label>
                          <input
                            type="text"
                            value={field.section || ''}
                            onChange={(e) => updateField(field.fieldId, { section: e.target.value })}
                            placeholder="e.g., Personal Information"
                          />
                        </div>

                        {['select', 'radio', 'multi_select'].includes(field.type) && (
                          <div className={styles.fieldRow}>
                            <label>Options (one per line)</label>
                            <textarea
                              value={(field.options || []).map((o) => typeof o === 'string' ? o : o.label).join('\n')}
                              onChange={(e) => updateField(field.fieldId, {
                                options: e.target.value.split('\n').filter(Boolean).map((label) => ({
                                  value: label.toLowerCase().replace(/\s+/g, '_'),
                                  label
                                }))
                              })}
                              placeholder="Option 1&#10;Option 2&#10;Option 3"
                              rows={4}
                            />
                          </div>
                        )}

                        <div className={styles.fieldRow}>
                          <label>Placeholder</label>
                          <input
                            type="text"
                            value={field.placeholder || ''}
                            onChange={(e) => updateField(field.fieldId, { placeholder: e.target.value })}
                            placeholder="Optional placeholder text"
                          />
                        </div>

                        <div className={styles.fieldRow}>
                          <label className={styles.checkbox}>
                            <input
                              type="checkbox"
                              checked={field.validation?.some((v) => v.type === 'required')}
                              onChange={() => toggleRequired(field.fieldId)}
                            />
                            <span>Required</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.fieldPreview}>
                        <div className={styles.fieldLabel}>
                          {field.label}
                          {field.validation?.some((v) => v.type === 'required') && (
                            <span className={styles.required}>*</span>
                          )}
                        </div>
                        <div className={styles.fieldMeta}>
                          <span className={styles.fieldType}>
                            {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
                          </span>
                          {field.section && (
                            <span className={styles.fieldSection}>{field.section}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Actions */}
            <div className={styles.actions}>
              <button type="button" className={styles.cancelButton} onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleSaveForm}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Form'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export default IntakeFormEditorModal;
